import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

function MaskedLine({ children, delay = 0, reduce }) {
  return (
    <span className="block overflow-hidden pb-[0.09em] -mb-[0.09em]">
      <motion.span
        className="block"
        initial={reduce ? { opacity: 1 } : { y: "112%" }}
        animate={reduce ? {} : { y: "0%" }}
        transition={{ duration: 0.9, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function GraphSvg() {
  const tiles = [
    [30, 60],
    [30, 140],
    [30, 220],
  ];
  const grid = [];
  for (let r = 0; r < 4; r++)
    for (let c = 0; c < 4; c++) grid.push([r, c]);
  return (
    <svg viewBox="0 0 460 420" fill="none" className="w-full">
      {tiles.map(([x, y], i) => (
        <g key={`t${i}`}>
          <rect x={x} y={y} width="64" height="34" stroke="rgba(255,255,255,0.25)" fill="rgba(255,255,255,0.03)" />
          <line x1={x + 8} y1={y + 10} x2={x + 40} y2={y + 10} stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
          <line x1={x + 8} y1={y + 18} x2={x + 28} y2={y + 18} stroke="rgba(255,255,255,0.18)" strokeWidth="2" />
          <line x1={x + 8} y1={y + 26} x2={x + 48} y2={y + 26} stroke="rgba(255,255,255,0.1)" strokeWidth="2" />
        </g>
      ))}
      <path d="M94 77 C 140 77, 150 128, 196 128" stroke="rgba(255,255,255,0.25)" className="flow" />
      <path d="M94 157 C 140 157, 150 142, 196 142" stroke="rgba(255,255,255,0.25)" className="flow" />
      <path d="M94 237 C 140 237, 150 244, 196 244" stroke="rgba(255,255,255,0.25)" className="flow" />
      <circle cx="210" cy="135" r="14" stroke="#10B981" fill="rgba(16,185,129,0.08)" />
      <circle cx="210" cy="244" r="14" stroke="rgba(255,255,255,0.3)" fill="rgba(255,255,255,0.03)" />
      <circle cx="210" cy="330" r="10" stroke="rgba(255,255,255,0.3)" fill="rgba(255,255,255,0.03)" />
      <path d="M224 135 C 272 135, 268 96, 310 96" stroke="rgba(255,255,255,0.25)" className="flow" />
      <path d="M224 244 C 272 244, 268 216, 310 216" stroke="rgba(255,255,255,0.25)" className="flow" />
      <path d="M220 330 C 270 330, 268 336, 310 336" stroke="rgba(16,185,129,0.45)" className="flow" />
      <rect x="312" y="60" width="122" height="120" stroke="rgba(255,255,255,0.12)" fill="rgba(255,255,255,0.01)" />
      {grid.map(([r, c]) => (
        <rect
          key={`g${r}${c}`}
          x={316 + c * 30}
          y={64 + r * 30}
          width="22"
          height="22"
          stroke="rgba(255,255,255,0.22)"
          fill={(r * 4 + c) % 5 === 0 ? "rgba(16,185,129,0.16)" : "rgba(255,255,255,0.02)"}
        />
      ))}
      <rect x="312" y="280" width="122" height="90" stroke="rgba(255,255,255,0.12)" fill="rgba(255,255,255,0.01)" />
      <text x="322" y="316" fontFamily="IBM Plex Mono, monospace" fontSize="9" letterSpacing="2" fill="rgba(255,255,255,0.45)">
        ACCEL
      </text>
      <line x1="322" y1="332" x2="424" y2="332" stroke="rgba(16,185,129,0.5)" strokeWidth="2" />
      <line x1="322" y1="348" x2="400" y2="348" stroke="rgba(255,255,255,0.2)" strokeWidth="2" />
      <text x="30" y="396" fontFamily="IBM Plex Mono, monospace" fontSize="10" letterSpacing="3" fill="rgba(255,255,255,0.4)">
        GRAPH → LOWER → TILES
      </text>
    </svg>
  );
}

export default function Hero() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 50, damping: 16 });
  const rY = useSpring(useTransform(mx, [-0.5, 0.5], [-5, 5]), { stiffness: 50, damping: 16 });
  const { scrollYProgress } = useScroll();
  const parY = useTransform(scrollYProgress, [0, 0.25], [0, reduce ? 0 : -60]);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => {
        mx.set(0);
        my.set(0);
      }}
      className="relative flex min-h-screen items-center overflow-hidden"
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 mask-fade bg-grid opacity-70" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl gap-12 px-6 pb-28 pt-32 lg:grid-cols-12 lg:px-10 lg:pt-24">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="mb-10 inline-flex items-center gap-3 border border-white/15 px-4 py-2"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent" />
            </span>
            <span data-testid="availability-badge" className="font-mono text-[10px] tracking-[0.25em] text-white/80">
              AVAILABLE FOR ONE CONTRACT ENGAGEMENT
            </span>
          </motion.div>

          <p className="mb-6 font-mono text-[11px] tracking-[0.3em] text-fog">ANDREAS FALKENBERG — PHD</p>

          <h1 className="font-display text-[clamp(3rem,7.5vw,6rem)] font-black leading-[0.94] tracking-[-0.02em] text-white">
            <MaskedLine delay={0.15} reduce={reduce}>Compilers for</MaskedLine>
            <MaskedLine delay={0.27} reduce={reduce}>custom AI</MaskedLine>
            <MaskedLine delay={0.39} reduce={reduce}>
              <span className="text-accent">silicon.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65, ease: EASE }}
            className="mt-8 max-w-xl text-base leading-relaxed text-fog sm:text-lg"
          >
            I put PyTorch and ONNX models onto bespoke accelerators — LLVM/MLIR
            lowering, graph partitioning and hardware-software co-design for
            AI-silicon teams.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8, ease: EASE }}
            className="mt-12 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-contact"
              onClick={() => scrollToId("contact")}
              className="group flex items-center gap-3 bg-white px-8 py-4 font-mono text-[11px] tracking-[0.2em] text-ink transition-colors hover:bg-accent"
            >
              GET IN TOUCH
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </button>
            <a
              data-testid="hero-cta-resume"
              href="/resume.pdf"
              download
              className="flex items-center gap-3 border border-white/20 px-8 py-4 font-mono text-[11px] tracking-[0.2em] text-white transition-colors hover:border-white"
            >
              RÉSUMÉ — PDF
            </a>
          </motion.div>
        </div>

        <div className="relative hidden items-center justify-center lg:col-span-5 lg:flex" style={{ perspective: 900 }}>
          <motion.div
            data-testid="hero-graph-visual"
            style={{ y: parY, rotateX: rX, rotateY: rY, transformStyle: "preserve-3d" }}
            className="w-full max-w-md"
          >
            <GraphSvg />
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-8 left-6 z-10 hidden items-center gap-3 font-mono text-[10px] tracking-[0.3em] text-fog sm:flex lg:left-10">
        SCROLL <ArrowDown className="h-3 w-3 animate-bounce" />
      </div>
    </section>
  );
}
